import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaWhatsapp, FaCheck, FaTimes } from 'react-icons/fa';
import './Packages.css';

const WHATSAPP = 'https://wa.me/918605246832?text=Hi%2C%20I%20want%20to%20know%20more%20about%20the%20';

const packages = [
  {
    id: 'starter',
    badge: '🟢 Starter',
    tag: 'Most Popular',
    featured: true,
    best: 'New Businesses & Startups',
    price: 'Starting @ ₹3,999',
    urgency: '⚡ Limited Time Offer',
    cta: 'Get Started Now',
    waText: 'Starter%20Package',
    items: [
      'Udyam (MSME) Registration',
      'Shop Act License',
      'FSSAI Registration (if applicable)',
      'Logo Design',
      '5-Page Static Website',
      '1 Month Social Media Setup',
    ],
  },
  {
    id: 'growth',
    badge: '🔵 Growth',
    tag: null,
    featured: false,
    best: 'Growing Businesses',
    price: '₹9,999 – ₹14,999',
    urgency: null,
    cta: 'Get Started Now',
    waText: 'Growth%20Package',
    items: [
      'Everything in Starter Package',
      'GST Registration',
      'Basic Digital Marketing Setup',
      'Social Media Pages Setup (FB + Instagram)',
      '3 Months GST Filing',
      'Business Email Setup',
    ],
  },
  {
    id: 'premium',
    badge: '🔴 Premium',
    tag: null,
    featured: false,
    best: 'Established Businesses',
    price: '₹25,000+',
    urgency: null,
    cta: 'Talk to Expert',
    waText: 'Premium%20Package',
    items: [
      'Everything in Growth Package',
      'Advanced Dynamic Website',
      'Meta Ads Setup (Instagram + Facebook)',
      '6 Months GST + ITR Filing',
      'Business Consulting (3 Sessions)',
      'Financial Projections Report',
    ],
  },
];

/* Comparison table data */
const tableFeatures = [
  { label: 'Udyam Registration',   starter: true,  growth: true,  premium: true  },
  { label: 'Shop Act License',     starter: true,  growth: true,  premium: true  },
  { label: 'Logo Design',          starter: true,  growth: true,  premium: true  },
  { label: 'Static Website',       starter: true,  growth: true,  premium: false },
  { label: 'Dynamic Website',      starter: false, growth: false, premium: true  },
  { label: 'GST Registration',     starter: false, growth: true,  premium: true  },
  { label: 'GST Filing (3 mo)',    starter: false, growth: true,  premium: true  },
  { label: 'GST + ITR (6 mo)',     starter: false, growth: false, premium: true  },
  { label: 'Social Media Setup',   starter: true,  growth: true,  premium: true  },
  { label: 'Meta Ads Setup',       starter: false, growth: false, premium: true  },
  { label: 'Business Consulting',  starter: false, growth: false, premium: true  },
  { label: 'Financial Projections',starter: false, growth: false, premium: true  },
];

function PackageCard({ pkg, index, inView }) {
  return (
    <motion.div
      className={`pkg-card${pkg.featured ? ' pkg-card--featured' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.15, ease: 'easeOut' }}
      whileHover={{ y: -6, boxShadow: pkg.featured ? '0 24px 60px rgba(201,168,76,0.35)' : '0 16px 40px rgba(0,0,0,0.12)' }}
    >
      {pkg.tag && <div className="pkg-card__tag">{pkg.tag}</div>}

      <div className="pkg-card__header">
        <span className="pkg-card__badge">{pkg.badge}</span>
        <p className="pkg-card__best">Best for: {pkg.best}</p>
      </div>

      <ul className="pkg-card__list">
        {pkg.items.map((item) => (
          <li key={item}>
            <FaCheck className="pkg-card__check" /> {item}
          </li>
        ))}
      </ul>

      <div className="pkg-card__footer">
        <div className="pkg-card__price">{pkg.price}</div>
        {pkg.urgency && <div className="pkg-card__urgency">{pkg.urgency}</div>}
        <motion.a
          href={`${WHATSAPP}${pkg.waText}`}
          target="_blank"
          rel="noopener noreferrer"
          className={pkg.featured ? 'btn-primary pkg-card__cta' : 'pkg-card__cta-outline'}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          <FaWhatsapp /> {pkg.cta}
        </motion.a>
      </div>
    </motion.div>
  );
}

export default function Packages() {
  const ref = useRef(null);
  const tableRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const tableInView = useInView(tableRef, { once: true, margin: '-60px' });

  return (
    <section className="section packages" id="packages">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          ref={ref}
        >
          <p className="section-label">Pricing</p>
          <h2 className="section-title">Business Packages</h2>
          <div className="gold-divider" />
          <p className="section-subtitle">Everything you need to launch and grow your business</p>
        </motion.div>

        <div className="packages__grid">
          {packages.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} inView={inView} />
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          className="pkg-table-wrap"
          ref={tableRef}
          initial={{ opacity: 0, y: 40 }}
          animate={tableInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3 className="pkg-table__title">Feature Comparison</h3>
          <div className="pkg-table-scroll">
            <table className="pkg-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>🟢 Starter</th>
                  <th className="pkg-table__featured-col">🔵 Growth</th>
                  <th>🔴 Premium</th>
                </tr>
              </thead>
              <tbody>
                {tableFeatures.map((f) => (
                  <tr key={f.label}>
                    <td>{f.label}</td>
                    <td>{f.starter ? <FaCheck className="tbl-yes" /> : <FaTimes className="tbl-no" />}</td>
                    <td className="pkg-table__featured-col">{f.growth ? <FaCheck className="tbl-yes" /> : <FaTimes className="tbl-no" />}</td>
                    <td>{f.premium ? <FaCheck className="tbl-yes" /> : <FaTimes className="tbl-no" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="packages__cta">
          <p className="packages__cta-note">Not sure which package to pick?</p>
          <a
            href="#contact"
            className="btn-primary"
            style={{ color: 'var(--navy)' }}
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            Book a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
